<script setup lang="ts">
import type { TDiamondItem } from '~/layers/catalog/data/types'

import { TCartItemType } from '~/layers/sale/data/types'
import WishlistButton from '~/layers/wishlist/components/WishlistButton.vue'
import { TWishlistItemType } from '~/layers/wishlist/data/types'

const props = defineProps<{ item: TDiamondItem }>()
const { $cart, $h } = useNuxtApp()
const filter = useCatalogFilter()
const itemId = computed(() => props.item.id)
const { toggle, isInCart } = $cart.context(TCartItemType.Diamond, itemId, {
  id: props.item.id.toString(),
  name: props.item.name,
  price: props.item.price,
})
const cartPending = ref(false)
const cartError = ref('')

const shapeIcons: Record<string, string | undefined> = {
  изумруд: 'brilliant-1',
  круглый: 'brilliant-2',
  овал: 'brilliant-3',
  груша: 'brilliant-4',
  маркиз: 'brilliant-5',
  принцесса: 'brilliant-6',
  сердце: 'brilliant-7',
  ашер: 'brilliant-8',
  радиант: 'icon-diamond-radiant',
  кушон: 'icon-diamond-cushion',
  'кв. изумруд': 'brilliant-8',
  'квадратный изумруд': 'brilliant-8',
  'прямоугольный багет': 'icon-diamond-baguette',
  'трапециевидный багет': 'icon-diamond-trap-baguette',
  багет: 'icon-diamond-trap-baguette',
}

const shapeName = computed(() => props.item.shape?.trim() || '')
const shapeIcon = computed(
  () => shapeIcons[shapeName.value.toLowerCase().replace(/\s+/g, ' ')],
)
const colorValue = computed(() =>
  filter.isEnabledRuValues('color') && props.item.colorRu
    ? props.item.colorRu
    : props.item.color,
)
const clarityValue = computed(() =>
  filter.isEnabledRuValues('clarity') && props.item.clarityRu
    ? props.item.clarityRu
    : props.item.clarity,
)

const isSingleWord = (value: string | number | null | undefined): boolean => {
  const text = String(value ?? '').trim()
  return text.length > 0 && !/\s/.test(text)
}

const openItem = () => {
  navigateTo(props.item.url)
}

const handleRowKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    openItem()
  }
}

const toggleCart = async () => {
  if (cartPending.value) return
  cartPending.value = true
  cartError.value = ''
  try {
    await toggle({ price: props.item.price, quantity: 1 })
  } catch {
    cartError.value = 'Не удалось обновить корзину. Попробуйте ещё раз.'
  } finally {
    cartPending.value = false
  }
}
</script>

<template>
  <tr
    :data-id="item.code"
    class="diamonds-table__row cursor-pointer"
    role="link"
    tabindex="0"
    @click="openItem"
    @keydown="handleRowKeydown"
  >
    <td
      class="diamonds-table__cell"
      :class="{ 'diamonds-table__cell--single-line': isSingleWord(item.code) }"
      :title="item.code || undefined"
    >
      <NuxtLink
        :to="item.url"
        @click.stop
        >{{ item.code || '—' }}</NuxtLink
      >
    </td>
    <td
      class="diamonds-table__cell diamonds-table__shape"
      :title="shapeName || undefined"
    >
      <div
        class="diamonds-table__shape-content flex h-full items-center gap-1"
        :class="{
          'diamonds-table__shape-content--single-line': isSingleWord(shapeName),
        }"
      >
        <svg-icon
          v-if="shapeIcon"
          :name="shapeIcon"
          class="shrink-0"
          height="24"
          width="24"
        />
        <span>{{ shapeName || '—' }}</span>
      </div>
    </td>
    <td
      class="diamonds-table__cell"
      :class="{
        'diamonds-table__cell--single-line': isSingleWord(item.weight),
      }"
      :title="item.weight || undefined"
    >
      {{ item.weight || '—' }}
    </td>
    <td
      class="diamonds-table__cell"
      :class="{ 'diamonds-table__cell--single-line': isSingleWord(colorValue) }"
      :title="colorValue || undefined"
    >
      {{ colorValue || '—' }}
    </td>
    <td
      class="diamonds-table__cell"
      :class="{
        'diamonds-table__cell--single-line': isSingleWord(clarityValue),
      }"
      :title="clarityValue || undefined"
    >
      {{ clarityValue || '—' }}
    </td>
    <td
      class="diamonds-table__cell"
      :class="{ 'diamonds-table__cell--single-line': isSingleWord(item.price) }"
      :title="
        item.isPriceHidden ? 'Под заказ' : $h.prettyPrice(item.price) || ''
      "
    >
      {{ item.isPriceHidden ? 'Под заказ' : $h.prettyPrice(item.price) }}
    </td>
    <td
      class="diamonds-table__cell diamonds-table__action"
      @click.stop
    >
      <ClientOnly>
        <button
          v-if="item.isAvailable || isInCart"
          class="relative"
          type="button"
          :aria-pressed="isInCart"
          :disabled="cartPending"
          @click="toggleCart"
        >
          <svg-icon
            height="24"
            name="icon-basket"
            width="24"
          />
          <span
            v-if="isInCart"
            class="absolute inset-x-0 top-[9px] text-center text-[10px] leading-3"
            aria-hidden="true"
            >1</span
          >
        </button>
        <NuxtLink
          v-else
          :to="item.url"
          >Под заказ</NuxtLink
        >
      </ClientOnly>
      <span
        v-if="cartError"
        role="alert"
        >{{ cartError }}</span
      >
    </td>
    <td
      class="diamonds-table__cell diamonds-table__action"
      @click.stop
    >
      <WishlistButton
        :id="item.id"
        :price="item.price"
        :type="TWishlistItemType.Diamond"
      />
    </td>
  </tr>
</template>
